import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ConfirmationDialogComponent, CustomDialog } from "@iventis/components";
import { DropDownOption } from "@iventis/components/src/nested-dropdown";
import { NodeEmptyComponent } from "@iventis/components/src/node-empty";
import { SideBarComponent } from "@iventis/components/src/sidebar";
import { SidebarItem } from "@iventis/components/src/sidebar-item";
import { SidebarItemsComponent } from "@iventis/components/src/sidebar-items";
import { Tools } from "@iventis/components/src/sidebar-tools";
import { AuthenticatedUser } from "@iventis/domain-model/model/authenticatedUser";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { AuthState } from "@iventis/permissions/src/auth.slice";
import { Header5, inputHeight } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { UserScope } from "@iventis/domain-model/model/userScope";
import { ItemOption } from "@iventis/components/src/sidebar-item-options";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { API_KEY_QUERY_PARAM_KEY, canEdit, isAdmin, queryParams } from "@iventis/utilities";
import { useQuery } from "@tanstack/react-query";
import { v4 as uuid } from "uuid";
import { shallowEqual, useSelector } from "react-redux";
import React, { FunctionComponent, useMemo, useState } from "react";
import { EventData, SCXML, SingleOrArray, Event } from "xstate";
import { toast } from "@iventis/toasts/src/toast";
import { exportUrlsKey, getExportUrls, updateScheduleVersionName } from "./schedule-api-functions";
import { getScheduleVersionColour } from "./schedule-helper-functions";
import { useDisciplineScheduleVersionPermission, useSchedulePermission, useScheduleVersionPermission } from "./schedule-hooks";
import { mockScheduleVersionTypes } from "./schedule-mock-data";
import { ScheduleMachineContext, ScheduleMachineEvent } from "./schedule.machine.types";
import { CloseIcon, HeaderContent, SidebarHeader, StyledScheduleVersionSelector } from "./schedule.styles";
import { ScheduleVersionNode, ScheduleWizardDisplayed } from "./schedule.types";
import { ShareScheduleFormWizard } from "./share-schedule-wizard";

export const ScheduleSidebar: FunctionComponent<{
    scheduleContext: ScheduleMachineContext;
    send: (event: SCXML.Event<ScheduleMachineEvent> | SingleOrArray<Event<ScheduleMachineEvent>>, payload?: EventData) => void;
    isLoading: boolean;
    isErrored: boolean;
    selectedScheduleNode: ScheduleVersionNode;
    width: number;
    close: () => void;
    setWizard: (value: { type: ScheduleWizardDisplayed; id?: string }) => void;
    onScheduleVisibilityChanged: () => void;
}> = ({ scheduleContext, send, selectedScheduleNode, setWizard, width, close, isLoading, isErrored, onScheduleVisibilityChanged }) => {
    const translate = useIventisTranslate();
    const currentUser: AuthenticatedUser = useSelector((state: { auth: AuthState }) => state.auth.user, shallowEqual);

    const { data: exportUrls } = useQuery([exportUrlsKey], getExportUrls, { enabled: currentUser?.projectAdmin, initialData: [] });
    const [openShareOptions, setOpenShareOptions] = useState<boolean>(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState(false);
    const handleDeleteConfirmed = () => {
        send({ type: "DELETE_SCHEDULE_VERSION", payload: scheduleContext.selectedSubScheduleIds[0] });
        send({ type: "SET_SELECTED_SUB_SCHEDULES", payload: [] });
        setDeleteConfirmation(false);
    };

    const allScheduleVersionSchedules: Hierarchy<ScheduleVersion>[] = scheduleContext?.availableSchedules?.children;
    const schedulePermission = useSchedulePermission(scheduleContext);
    const disciplineScheduleVersionPermission = useDisciplineScheduleVersionPermission(scheduleContext);
    const scheduleVersionPermission = useScheduleVersionPermission(scheduleContext);

    const deleteTextConfirmation = useMemo(
        () => ({
            title: translate(Content.schedule2.delete_schedule_version.title),
            message: translate(Content.schedule2.delete_schedule_version.message, {
                scheduleVersionName: selectedScheduleNode?.childNodes.find((child) => child.id === scheduleContext.selectedSubScheduleIds[0])?.name,
            }),
            confirm: translate(Content.common.buttons.confirm),
            cancel: translate(Content.common.buttons.cancel),
        }),
        [translate]
    );

    const tools: Tools = {
        left: [
            {
                icon: ["fas", "books"],
                label: translate(Content.schedule.edit_schedule_data_options.title),
                disabled: !currentUser.projectAdmin,
                onClick: () => setWizard({ type: ScheduleWizardDisplayed.ADD_DATAFIELDS, id: scheduleContext.selectedScheduleVersionRootId }),
            },
            {
                icon: ["fas", "upload"],
                label: translate(Content.schedule2.sidebar.upload),
                disabled:
                    scheduleContext.selectedSubScheduleIds?.length === 0
                        ? !(canEdit(selectedScheduleNode?.authorisation) && selectedScheduleNode?.childNodes.length > 0)
                        : scheduleContext.selectedSubScheduleIds.every((id) => !canEdit(selectedScheduleNode.childNodes.find((node) => node.id === id)?.authorisation)),
                onClick: () => setWizard({ type: ScheduleWizardDisplayed.IMPORT_EVENT_BLOCKS }),
            },
            {
                icon: ["far", "share-nodes"],
                label: translate(Content.common.buttons.share),
                onClick: () => setOpenShareOptions(true),
                disabled: queryParams[API_KEY_QUERY_PARAM_KEY] != null,
            },
        ],
        right: [
            {
                icon: ["far", "file-plus"],
                label: translate(Content.common.buttons.add),
                onClick: () =>
                    Boolean(mockScheduleVersionTypes.subSchedule) && isAdmin(scheduleVersionPermission) && setWizard({ type: ScheduleWizardDisplayed.CREATE_SUB_SCHEDULE }),
                disabled: !isAdmin(scheduleVersionPermission),
            },
            {
                icon: ["far", "trash"],
                label: translate(Content.common.buttons.delete),
                disabled: scheduleContext.selectedSubScheduleIds.length < 1 || !isAdmin(disciplineScheduleVersionPermission),
                onClick: () => setDeleteConfirmation(true),
            },
        ],
    };

    const scheduleOptions: DropDownOption[] = [
        {
            name: translate(Content.schedule.manage_schedule_settings),
            onSelection: () => setWizard({ type: ScheduleWizardDisplayed.EDIT_SCHEDULE, id: scheduleContext.selectedScheduleVersionRootId }),
            dataCy: "manage-schedule-settings",
            disabled: currentUser?.userScope === UserScope.External, // External users dont have access to hierarchy permissions so they cannot load manage schedule without errors. Application read users can as they may need to see who to ask permission from.
        },
        {
            name: translate(Content.schedule.create_new_schedule),
            onSelection: () => setWizard({ type: ScheduleWizardDisplayed.CREATE_SCHEDULE }),
            disabled: !isAdmin(schedulePermission),
        },
    ];

    const scheduleDropdownOptions: DropDownOption[] = allScheduleVersionSchedules
        ?.filter((hierarchyItem) => hierarchyItem.item !== null)
        .map(({ item: scheduleItem }) => ({
            name: scheduleItem.name,
            id: scheduleItem.id,
            colour: getScheduleVersionColour(scheduleItem),
            onSelection: (id) => send({ type: "SELECT_ROOT_SCHEDULE_VERSION", payload: id }),
            dataCy: "schedule-version-item",
        }));

    const waitingForSelectedSchedule = scheduleContext.pendingSchedules.find(({ rootScheduleVersion }) => rootScheduleVersion.id === selectedScheduleNode.id);

    const waitingForSubSchedules: SidebarItem[] = useMemo(
        () =>
            waitingForSelectedSchedule?.scheduleVersionsPending.map(({ name }) => ({
                id: uuid(),
                name,
                selected: false,
                editName: false,
                allowEditName: false,
                visible: true,
                pending: true,
                singleSelection: true,
            })) || [],
        [waitingForSelectedSchedule, waitingForSelectedSchedule?.scheduleVersionsPending]
    );

    // schedule version visibility is changed
    const handleVisibilityChange = (id: string) => {
        send({ type: "UPDATE_SCHEDULE_VERSION_VISIBILITY", payload: id });
        onScheduleVisibilityChanged();
    };

    const handleItemNameChange = (scheduleVersionId: string, scheduleVersionName: string) => {
        updateScheduleVersionName(scheduleVersionId, scheduleVersionName);
        scheduleVersionsSidebar.find(({ id }) => id === scheduleVersionId).nameChange(scheduleVersionId, scheduleVersionName);
    };

    // schedule version edit name change
    const handleChangeEditName = (id: string, value: boolean) => {
        send({ type: "UPDATE_SCHEDULE_VERSION_EDIT_NAME", payload: { id, value } });
    };

    // settings cog is clicked
    const handleSettingsClick = (id: string) => {
        setWizard({ type: ScheduleWizardDisplayed.EDIT_SCHEDULE, id });
    };

    const scheduleVersionSidebarItemOptions = (scheduleVersionNode: ScheduleVersionNode) => {
        const settings = {
            id: "settings",
            name: translate(Content.schedule.settings),
            icon: ["fas", "gear"] as IconProp,
            onClick: handleSettingsClick,
        };
        const visiblity = {
            id: "visible",
            name: translate(Content.schedule.visibility),
            icon: (scheduleVersionNode.visible ? ["fas", "eye"] : ["fas", "eye-slash"]) as IconProp,
            show: !scheduleVersionNode.visible,
            onClick: handleVisibilityChange,
        };

        const options: ItemOption[] = [visiblity];
        if (currentUser?.userScope !== UserScope.External) {
            options.push(settings);
        }
        return options;
    };

    const scheduleVersionsSidebar: SidebarItem[] = [
        ...(selectedScheduleNode?.childNodes.map((child) => ({
            id: child.id,
            name: child.name,
            selected: scheduleContext.selectedSubScheduleIds.some((disciplineSchedule) => disciplineSchedule === child.id),
            singleSelection: true,
            editName: child.editName,
            visible: child.visible,
            allowEditName: canEdit(child.authorisation),
            nameChange: (id, name) => send({ type: "UPDATE_SCHEDULE_VERSION_NAME", payload: { id, name } }),
            changeEditName: handleChangeEditName,
            dataCy: `discipline-schedule-${child.name}`,
            pending: false,
            itemOptions: scheduleVersionSidebarItemOptions(child),
            onClick: (id) =>
                send({
                    type: "SET_SELECTED_SUB_SCHEDULES",
                    payload: scheduleContext.selectedSubScheduleIds.includes(id) ? scheduleContext.selectedSubScheduleIds.filter((subSchedule) => subSchedule !== id) : [id],
                }),
        })) || []),
        ...waitingForSubSchedules,
    ].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <SideBarComponent minimumWidth="200px" startingWidth={width} tools={tools}>
            <SidebarHeader>
                <HeaderContent>
                    <FontAwesomeIcon icon={["fas", "list-ul"]} />
                    <Header5 className="header-title">{translate(Content.schedule.schedule)}</Header5>
                    <CloseIcon>
                        <FontAwesomeIcon icon={["fas", "xmark"]} onClick={close} />
                    </CloseIcon>
                </HeaderContent>
            </SidebarHeader>
            {scheduleContext.selectedScheduleVersionRootId && (
                <StyledScheduleVersionSelector
                    className="schedule-selector"
                    icon={["fas", "circle"]}
                    selectedId={scheduleContext.selectedScheduleVersionRootId}
                    optionGroup={[scheduleDropdownOptions, scheduleOptions]}
                    disabled={isLoading || isErrored}
                    dataCy="schedule-selector"
                    menuClasses={{ list: "schedule-selector-list" }}
                />
            )}
            {scheduleVersionsSidebar?.length === 0 && selectedScheduleNode ? (
                <NodeEmptyComponent
                    node={selectedScheduleNode}
                    typeName={translate(
                        scheduleContext.schedule.config.scheduleVersionTypes.find((t) => t.allowedParentType === scheduleContext.selectedScheduleVersionHierarchy.item.type.id)
                            ?.name
                    )}
                />
            ) : (
                <SidebarItemsComponent
                    blockInteractivity={isLoading}
                    items={scheduleVersionsSidebar}
                    onItemNameChange={handleItemNameChange}
                    sidebarItemHeight={inputHeight}
                    onInvalidNameInputted={() => toast.info({ title: translate(Content.common.alert), message: translate(Content.map.sidebar.node_name) })}
                />
            )}
            {openShareOptions && (
                <CustomDialog open={openShareOptions} onClose={() => setOpenShareOptions(false)}>
                    <ShareScheduleFormWizard
                        scheduleVersionId={scheduleContext.selectedScheduleVersionRootId}
                        projectAdmin={currentUser.projectAdmin}
                        exportUrls={exportUrls}
                        close={() => setOpenShareOptions(false)}
                        projectId={currentUser.projectId}
                    />
                </CustomDialog>
            )}
            <ConfirmationDialogComponent
                show={deleteConfirmation}
                title={deleteTextConfirmation.title}
                message={deleteTextConfirmation.message}
                confirmText={deleteTextConfirmation.confirm}
                cancelText={deleteTextConfirmation.cancel}
                handleCancel={() => setDeleteConfirmation(false)}
                handleConfirm={handleDeleteConfirmed}
            />
        </SideBarComponent>
    );
};
